import React, {useState, useEffect} from 'react';
import {useNavigate, Link} from 'react-router-dom';

import {useAuth} from './components/kdl/security/AuthContext';
import {apiClient} from './ApiClient';
import './LoginComponent.css';

function LoginComponent() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();
    const authContext = useAuth();

    // Token validation and profile fetching
    useEffect(() => {
        const fetchProfiles = async () => {
            try {
                const response = await apiClient.get('/rest/photofastlogin');
                setProfiles(response.data.photofastlogin || []);
            } catch (error) {
                console.error('Error fetching profiles:', error);
                // 401 errors will be handled by interceptor automatically
            } finally {
                setLoading(false);
            }
        };

        fetchProfiles();
        
        // Check for OAuth callback
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        
        if (code) {
            console.log('Found OAuth code, processing login...');
            // Clean URL immediately to prevent reprocessing
            window.history.replaceState({}, document.title, '/login');
            handleOAuthCallback(code);
        }
    }, [authContext]);

    function handleUsernameChange(event) {
        setUsername(event.target.value);
    }

    function handlePasswordChange(event) {
        setPassword(event.target.value);
    }

    async function handleLogin() {
        // Clear any existing JWT token before attempting login
        localStorage.removeItem('token');
        const loginResponse = await authContext.login(username, password);
        if (loginResponse) {
            localStorage.setItem('username', username);
            navigate('/');
        } else {
            setShowErrorMessage(true);
        }
    }

    function handleCreateAccount() {
        // Clear token from localStorage
        localStorage.removeItem('token');

        // Clear auth context state
        authContext.setIsAuthenticated(false);
        authContext.setToken(null);
        authContext.setUsername('');

        // Navigate to signup page
        navigate('/signup');
    }

    function handleKeyPress(e) {
        if (e.key === 'Enter') {
            handleLogin();
        }
    }

    // Handle OAuth callback - SINGLE FUNCTION ONLY
    async function handleOAuthCallback(code) {
        try {
            setLoading(true);
            
            // Send authorization code to your backend
            const response = await apiClient.post('/auth/google/callback', {
                code: code,
                redirect_uri: window.location.origin + '/login'
            });

            if (response.data && response.data.token) {
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('username', response.data.user.username);

                authContext.setIsAuthenticated(true);
                authContext.setToken(response.data.token);
                authContext.setUsername(response.data.user.username);
                
                navigate('/');
            }
        } catch (error) {
            console.error('OAuth callback failed:', error);
            setShowErrorMessage(true);
        } finally {
            setLoading(false);
        }
    }

    // Google OAuth redirect - Force old OAuth flow that works
    function handleGoogleButtonClick() {
        const clientId = '718883682303-4fs23bsn30uogmjd0cm0c0har99knrjd.apps.googleusercontent.com';
        const redirectUri = encodeURIComponent(window.location.origin + '/login');
        const scope = encodeURIComponent('profile email');
        
        // Force the old OAuth flow that works (same as iPad gets)
        const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
            `client_id=${clientId}&` +
            `redirect_uri=${redirectUri}&` +
            `response_type=code&` +
            `scope=${scope}&` +
            `access_type=online&` +
            `prompt=select_account&` +
            `include_granted_scopes=true&` +
            `ux_mode=redirect&` +
            `disable_fedcm=1`;  // Force disable problematic FedCM
        
        console.log('Redirecting to Google OAuth (forced old flow)...');
        window.location.href = googleAuthUrl;
    }

    return (
        <div className="login-main-content">
            <div className="login-grid">
                {/* Left Column - Login Form */}
                <div className="login-left-column">
                    <div className="login-box">
                        <h2 className="login-title">Log in</h2>

                        <div className="form-field">
                            <input
                                type="text"
                                placeholder="Username"
                                className="form-input"
                                value={username}
                                onChange={handleUsernameChange}
                                onKeyPress={handleKeyPress}
                            />
                        </div>

                        <div className="form-field">
                            <input
                                type="password"
                                placeholder="Password"
                                className="form-input"
                                value={password}
                                onChange={handlePasswordChange}
                                onKeyPress={handleKeyPress}
                            />
                        </div>

                        {/* Login button */}
                        <button
                            className="auth-button login-button"
                            onClick={handleLogin}
                        >
                            <span className="heart-icon">❤️</span> Sign in
                        </button>

                        {showErrorMessage &&
                            <div className="error-message">Invalid Username Password</div>
                        }

                        <div className="separator-text">
                            Social Login
                        </div>

                        {/* Google button */}
                        <button
                            className="auth-button google-button"
                            onClick={handleGoogleButtonClick}
                        >
                            <span className="google-icon"></span>Sign in with Google
                        </button>

                        {/* Create account button */}
                        <button
                            className="auth-button create-account-button"
                            onClick={handleCreateAccount}
                        >
                            <span className="user-plus-icon">👤+</span> Create new account
                        </button>

                        <div className="divider"></div>

                        <div className="info-section">
                            <h3>KissDateLove</h3>
                            <p>
                                KissDateLove is fast becoming a leader in the online dating
                                industry because of our advanced features and ease of use.
                                This is your chance to create a meaningful lasting relationship
                                at no cost and no contract (FREE).
                            </p>
                            <p>
                                KissDateLove has plenty of on-line personals for members to
                                browse through and meet that special someone. Every day new
                                singles join our growing community.
                            </p>
                            <div className="benefits">
                                <p>- KissDateLove is 100% FREE</p>
                                <p>- No Credit Card required</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column - Profile Grid */}
                <div className="login-right-column">
                    <div className="profiles-container">
                        {loading ? (
                            <div className="loading">Loading profiles...</div>
                        ) : (
                            <div className="profiles-grid">
                                {profiles.map((profile, index) => (
                                    <div className="profile-item" key={index}>
                                        <div className="profile-image-container">
                                            <img
                                                className="profile-image"
                                                src={`https://pics.netfixup.com/${profile.IMAGE_PATH || ''}${profile.MEMBER_IMAGE_NAME || ''}`}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg';
                                                }}
                                                alt="Profile"
                                            />
                                        </div>
                                        <div className="profile-info">
                                            <div>{profile.MEMBER_AGE || '?'}-Seeking {profile.DESC_LOOKINGFOR || '?'}</div>
                                            <div>Age {profile.AGE_RANGE_START || '?'} to {profile.AGE_RANGE_END || '?'}</div>
                                            <div>{profile.ZIP_CITYNAME?.substring(0, 11) || '?'}, {profile.ZIP_STATEABBR || '?'}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoginComponent;